import React, { useEffect, useRef } from 'react';
import '../styles/chat.css';

const OutreachLogs = ({
  summary,
  logs = [],
  isSending = false,
  isDark = false,
  primaryColor = '#3B5BDB',
  secondaryColor = '#20C997',
  totalCandidatesFallback = 0,
}) => {
  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [logs]);

  const total =
    summary?.total ||
    summary?.totalCandidates ||
    totalCandidatesFallback ||
    0;
  const sent = summary?.sent || 0;
  const failed = summary?.failed || 0;

  const emailSent = summary?.email_sent ?? summary?.emailSent ?? 0;
  const emailFailed = summary?.email_failed ?? summary?.emailFailed ?? 0;
  const linkedinSent = summary?.linkedin_sent ?? summary?.linkedinSent ?? 0;
  const linkedinFailed = summary?.linkedin_failed ?? summary?.linkedinFailed ?? 0;
  const whatsappSent = summary?.whatsapp_sent ?? summary?.whatsappSent ?? 0;
  const whatsappFailed = summary?.whatsapp_failed ?? summary?.whatsappFailed ?? 0;

  // Check if channels are active (have any sent/failed messages)
  const emailActive = emailSent > 0 || emailFailed > 0;
  const linkedinActive = linkedinSent > 0 || linkedinFailed > 0;
  const whatsappActive = whatsappSent > 0 || whatsappFailed > 0;

  const emailTotal = emailSent + emailFailed;
  const linkedinTotal = linkedinSent + linkedinFailed;
  const whatsappTotal = whatsappSent + whatsappFailed;

  const completedCount = sent;
  const completedPercent =
    total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <div className="chat_spacing_sm">
      {/* Title - Centered */}
      <h3
        className="chat_heading_md chat_font_semibold chat_mb_lg text-center"
        style={{ color: primaryColor, marginBottom: '32px' }}
      >
        Live Outreach In Progress
      </h3>

      {/* Two Cards Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '44px' }}>
        {/* Summary Card */}
        <div
          style={{
            backgroundColor: isDark ? 'rgba(59, 91, 219, 0.1)' : 'rgba(59, 91, 219, 0.08)',
            borderRadius: '18px',
            padding: '18px 16px',
            minHeight: '124px',
            border: `1px solid ${isDark ? 'rgba(59, 91, 219, 0.35)' : '#8EA2FF'}`,
          }}
        >
          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '18px', lineHeight: '100%', color: isDark ? '#F9FAFB' : '#111827' }}>
            Summary
          </h4>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', marginBottom: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span>Candidates Selected: <span style={{ fontWeight: 600, color: isDark ? '#F3F4F6' : '#111827' }}>{total}</span></span>
            </li>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', marginBottom: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span>
                Channels:{' '}
                <span style={{ fontWeight: 600, color: isDark ? '#F3F4F6' : '#111827' }}>
                  {(() => {
                    const channels = [];
                    if (emailSent > 0) {
                      channels.push(`Email (${emailSent})`);
                    }
                    if (linkedinSent > 0) {
                      channels.push(`LinkedIn InMail (${linkedinSent})`);
                    }
                    if (whatsappSent > 0) {
                      channels.push(`WhatsApp (${whatsappSent})`);
                    }
                    if (channels.length === 0) {
                      return 'Email (primary)';
                    }
                    return channels.join(', ');
                  })()}
                </span>
              </span>
            </li>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span>Personalization Level: <span style={{ fontWeight: 600, color: isDark ? '#F3F4F6' : '#111827' }}>High (JD + profile signals)</span></span>
            </li>
          </ul>
        </div>

        {/* Live Sending Status Card */}
        <div
          style={{
            backgroundColor: isDark ? 'rgba(59, 91, 219, 0.1)' : 'rgba(59, 91, 219, 0.08)',
            borderRadius: '18px',
            padding: '18px 16px',
            minHeight: '124px',
            border: `1px solid ${isDark ? 'rgba(59, 91, 219, 0.35)' : '#8EA2FF'}`,
          }}
        >
          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '18px', lineHeight: '100%', color: isDark ? '#F9FAFB' : '#111827' }}>
            Live Sending Status
          </h4>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', marginBottom: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span style={{ fontWeight: 600 }}>{isSending ? 'Sending personalized messages…' : 'Outreach completed successfully.'}</span>
            </li>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', marginBottom: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span>
                Email delivery:{' '}
                <span style={{ color: '#16A34A', fontWeight: 600 }}>
                  {emailActive ? 'Active' : 'Not selected'}
                </span>
              </span>
            </li>
            <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
              <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
              <span>
                LinkedIn InMail queue:{' '}
                <span style={{ color: '#16A34A', fontWeight: 600 }}>
                  {linkedinActive ? 'Active' : 'Not selected'}
                </span>
              </span>
            </li>
            {whatsappActive && (
              <li style={{ fontSize: '12px', fontWeight: 400, color: isDark ? '#E5E7EB' : '#111827', marginTop: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '120%' }}>
                <span style={{ color: '#6B7280', marginTop: '1px' }}>•</span>
                <span>
                  WhatsApp queue:{' '}
                  <span style={{ color: '#16A34A', fontWeight: 600 }}>Active</span>
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Mini Activity Feed Section */}
      <div>
        <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '16px', color: isDark ? '#F3F4F6' : '#374151' }}>
          Mini Activity Feed
        </h4>

        {logs.length === 0 ? (
          <div
            style={{
              backgroundColor: isDark ? 'rgba(59, 91, 219, 0.1)' : '#E2E8FF',
              borderRadius: '16px',
              padding: '16px',
              border: `1px solid ${isDark ? 'rgba(59, 91, 219, 0.3)' : `transparent`}`,
              borderImage: isDark ? 'none' : `linear-gradient(135deg, ${primaryColor}, ${secondaryColor}) 1`,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px',
            }}
          >
            <span style={{ fontSize: '18px' }}>⏳</span>
            <p style={{ fontSize: '13px', color: isDark ? '#9CA3AF' : '#6B7280', margin: 0 }}>
              Waiting for outreach activity...
            </p>
          </div>
        ) : (
          <>
            {/* Figma frame: 656x80 (desktop), vertical gap 16 */}
            <div
              style={{
                width: '100%',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: isDark ? 'rgba(59, 91, 219, 0.12)' : '#DDEFFF',
                borderRadius: '14px',
                border: `1px solid transparent`,
                backgroundImage: isDark ? 'none' : `linear-gradient(#DDEFFF, #DDEFFF), linear-gradient(135deg, #8EC5E9, #6EC6BE)`,
                backgroundOrigin: isDark ? 'unset' : 'border-box',
                backgroundClip: isDark ? 'unset' : 'padding-box, border-box',
                boxSizing: 'border-box',
              }}
            >
              {/* Text row: width 615, 12/20 semi-bold */}
              <p
                style={{
                  width: '100%',
                  minHeight: '20px',
                  margin: 0,
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: '20px',
                  letterSpacing: '0',
                }}
              >
                {logs.map((log, idx) => {
                  const msg = (log.message || '').trim();
                  const isPreparing = msg.toLowerCase().includes('preparing');
                  const isError = log.success === false;
                  const finalColor = isError ? '#DC2626' : isPreparing ? '#F08C00' : '#2F9E44';
                  return (
                    <span key={idx}>
                      <span style={{ color: finalColor, fontWeight: 600 }}>{msg}</span>
                      {idx < logs.length - 1 && <span style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}> | </span>}
                    </span>
                  );
                })}
              </p>

              {/* Progress row frame: fill(624), hug(15), horizontal gap 13 */}
              <div
                style={{
                  width: '100%',
                  minHeight: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 400, lineHeight: '15px', whiteSpace: 'nowrap', color: isDark ? '#E5E7EB' : '#111827' }}>
                  Outreach Progress:{' '}
                  <span style={{ color: primaryColor, fontWeight: 600, fontStyle: 'italic' }}>
                    {completedCount} / {total} Sent ({completedPercent}%)
                  </span>
                </span>
                <div
                  style={{
                    flex: 1,
                    height: '8px',
                    backgroundColor: isDark ? '#374151' : '#B9E7E1',
                    borderRadius: '999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${completedPercent}%`,
                      background: completedPercent === 100
                        ? '#16A34A'
                        : `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                      borderRadius: '999px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OutreachLogs;
